/** @file Starting arguments of an analysis from the selection: the selected slot's attachment, bone, pairs. */
import type { AutorigFn } from '@pose2d/api/autorig/autorigFns';
import type { Doc, Id } from '@pose2d/format/types';
import type { SelectionSlice } from '../../stores/types';
import { actionOf } from './actionOf';
import { argOptions } from './argOptions';
import type { ArgField, ArgKind } from './autorigTypes';

type Picked = Pick<SelectionSlice, 'bones' | 'slots'>;

/** The attachment the first selected slot shows in setup (default skin), when the field accepts it. */
function shownAttachment(doc: Doc, selection: Picked, field: ArgField): string | undefined {
  const slotId = selection.slots[0];
  const slot = slotId === undefined ? undefined : doc.slots[slotId];
  const placeholder = slot?.setup.attachment ?? null;
  const entry = doc.skins[doc.defaultSkin]?.entries.find((e) => e.slot === slotId && e.placeholder === placeholder);
  const name = entry === undefined ? undefined : doc.attachments[entry.attachment]?.name;
  return name !== undefined && argOptions(doc, field).includes(name) ? name : undefined;
}

/** [parent, child] name pairs of selected bones whose parent is selected too (detectJoints `pairs`). */
function selectedPairs(doc: Doc, bones: readonly Id[]): [string, string][] | undefined {
  const chosen = new Set(bones);
  const pairs = bones.flatMap((id): [string, string][] => {
    const bone = doc.bones[id];
    const parent = bone?.parent ?? null;
    const parentName = parent !== null && chosen.has(parent) ? doc.bones[parent]?.name : undefined;
    return bone === undefined || parentName === undefined ? [] : [[parentName, bone.name]];
  });
  return pairs.length === 0 ? undefined : pairs;
}

/** The kinds a selection can fill; every other kind starts empty (the analysis default). */
const DEFAULTS: Partial<Record<ArgKind, (doc: Doc, selection: Picked, field: ArgField) => unknown>> = {
  attachment: shownAttachment,
  bone: (doc, selection) => (selection.bones[0] === undefined ? undefined : doc.bones[selection.bones[0]]?.name),
  pairs: (doc, selection) => selectedPairs(doc, selection.bones),
};

/** The form's starting values for `fn`; only defined values are present. */
export function defaultArgs(fn: AutorigFn, doc: Doc, selection: Picked): Record<string, unknown> {
  const args: Record<string, unknown> = {};
  for (const field of actionOf(fn).fields) {
    const value = DEFAULTS[field.kind]?.(doc, selection, field);
    if (value !== undefined) args[field.key] = value;
  }
  return args;
}
